import { z } from "zod";
import type { ClinicSettings } from "./settings";
import type { PermissionKey } from "./permissions";

/** A staff member's mailing address (Staff → name, Your account, and their W-9). */
export const staffAddressSchema = z.object({
  line1: z.string().trim().min(1, "Enter the street address").max(120),
  line2: z.string().trim().max(60).default(""),
  city: z.string().trim().min(1, "Enter the city").max(60),
  state: z.string().trim().min(2, "Enter the state").max(30),
  postalCode: z.string().trim().min(3, "Enter the ZIP").max(12),
});
export type StaffAddress = z.output<typeof staffAddressSchema>;
/** "1 Main St Apt 2" (line 5 of a W-9). */
export const staffStreet = (a: Pick<StaffAddress, "line1" | "line2">) => [a.line1, a.line2].filter(Boolean).join(" ");
/** "Oklahoma City, OK 73116" (line 6 of a W-9). */
export const staffCityLine = (a: Pick<StaffAddress, "city" | "state" | "postalCode">) => `${a.city}, ${a.state} ${a.postalCode}`;
/** "1 Main St Apt 2, Oklahoma City, OK 73116". */
export const formatStaffAddress = (a: StaffAddress) => `${staffStreet(a)}, ${staffCityLine(a)}`;

/** Shapes exchanged between the web app and the API. */

export interface MeResponse {
  staff: { id: string; email: string; firstName: string; lastName: string };
  clinic: { id: string; name: string; timezone: string };
  permissions: PermissionKey[];
  settings: Pick<ClinicSettings, "security.inactivityTimeoutMinutes" | "branding.primaryColor" | "branding.accentColor"> & {
    logoUrl: string | null;
  };
  sessionExpiresAt: string;
  /** Staging's test mode: where texts and email go instead (shown as a banner); null = off. */
  testMode: string | null;
  /** "View as": the app is showing this staff member's view to someone else (read-only) until then; null = off. */
  viewingAs: { viewerName: string; until: string } | null;
  /** Paperwork a manager asked them for (a banner on Today until done). */
  paperwork: { w9: boolean; contractToSign: boolean; contractsToCountersign: number };
}

export interface SecurityResponse {
  /** False in local development, where there are no real accounts. */
  available: boolean;
  passkeys: { id: string; name: string; createdAt: string | null; authenticator: string | null }[];
  /** The second step after a password: authenticator app, emailed code, or not set up yet. */
  mfa: { method: "totp" | "email" | "none"; totpEnabled: boolean; emailEnabled: boolean };
}

export interface StaffListItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  /** Colour on the Availability page; null = from the list order. */
  color: string | null;
  status: "invited" | "active" | "deactivated";
  roleIds: string[];
  lastSignInAt: string | null;
  /** An invite email has gone out (added with "Email their invite now", or Send invite since). */
  inviteSent: boolean;
}

export interface StaffDetail extends StaffListItem {
  credentials: string;
  /** On the clinic's website (the public feed): shown, title, short bio, headshot. */
  website: { show: boolean; title: string; bio: string; photoKey: string | null; photoUrl: string | null };
  /** Saved signature (PNG data URL), for school and work notes. */
  signature: string | null;
  /** Mailing address; null when not on file. */
  address: StaffAddress | null;
  overrides: { permission: string; effect: "grant" | "revoke" }[];
  effectivePermissions: PermissionKey[];
}

export interface RoleItem {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  staffCount: number;
}

export interface AuditEntry {
  id: string;
  occurredAt: string;
  actorName: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  details: Record<string, unknown>;
  ip: string | null;
}

export const nameSchema = z.string().trim().min(1).max(80);

export const inviteStaffSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  firstName: nameSchema,
  lastName: nameSchema,
  phone: z.string().trim().max(30).optional().nullable(),
  roleIds: z.array(z.string().uuid()).min(1, "Pick at least one role"),
  /** Email the sign-in invite now; when false, send it later from their staff page. */
  sendInvite: z.boolean().default(true),
});

export const updateStaffSchema = z.object({
  firstName: nameSchema.optional(),
  lastName: nameSchema.optional(),
  phone: z.string().trim().max(30).nullable().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  credentials: z.string().trim().max(60).optional(),
  /** null clears it. */
  address: staffAddressSchema.nullable().optional(),
  showOnWebsite: z.boolean().optional(),
  websiteTitle: z.string().trim().max(80).optional(),
  bio: z.string().trim().max(2000).optional(),
  /** From POST /api/website/photo-upload; null takes the photo off. */
  photoKey: z.string().max(300).nullable().optional(),
  roleIds: z.array(z.string().uuid()).optional(),
  overrides: z
    .array(z.object({ permission: z.string(), effect: z.enum(["grant", "revoke"]) }))
    .optional(),
});

export const roleInputSchema = z.object({
  name: nameSchema,
  description: z.string().trim().max(300).default(""),
  permissions: z.array(z.string()),
});

export const clinicProfileSchema = z.object({
  name: nameSchema.optional(),
  timezone: z.string().min(1).max(64).optional(),
  /** Short identifier used in public links, e.g. vaulted-vitality. */
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Use lowercase letters, numbers, and dashes")
    .min(3)
    .max(40)
    .optional(),
});
